import { makeChars } from "./teletext_data.js";
import { makeFast32 } from "./utils.js";
import { BbcDefaultPalette as BbcDefaultCollook } from "./bbc-palette.js";

export class Teletext {
    constructor() {
        this.prevCol = 0;
        this.col = 7;
        this.bg = 0;
        this.sep = false;
        this.dbl = this.oldDbl = this.secondHalfOfDouble = this.wasDbl = false;
        this.gfx = false;
        this.conceal = false;
        this.flash = false;
        this.flashTime = 0;
        this.heldChar = 0;
        this.holdChar = false;
        this.cellGlyphIndex = 0;
        this.cellFlash = false;
        this.cellConceal = false;
        this.cellPalette = 0;
        this.dataQueue = new Uint8Array(4);
        this.scanlineCounter = 0;
        this.levelDEW = false;
        this.levelDISPTMG = false;
        this.levelRA0 = false;

        this.normalGlyphs = makeFast32(new Uint32Array(96 * 20));
        this.graphicsGlyphs = makeFast32(new Uint32Array(96 * 20));
        this.separatedGlyphs = makeFast32(new Uint32Array(96 * 20));
        this.colour = makeFast32(new Uint32Array(256));

        this.nextGlyphs = this.normalGlyphs;
        this.curGlyphs = this.normalGlyphs;
        this.heldGlyphs = this.normalGlyphs;

        this.init();
    }

    init() {
        const charData = makeChars();

        // Build palette from BBC default colours.
        this.rebuildColours(BbcDefaultCollook);

        function getLoResGlyphRow(c, row) {
            if (row < 0 || row >= 20) {
                return 0;
            } else {
                let index = c * 60 + (row >>> 1) * 6;
                let result = 0;
                for (let x = 0; x < 6; ++x) {
                    result |= (charData[index++] * 3) << (x * 2);
                }
                return result;
            }
        }

        function combineRows(a, b) {
            return a | ((a >>> 1) & b & ~(b >>> 1)) | ((a << 1) & b & ~(b << 1));
        }

        function makeHiResGlyphs(dest, graphicsGlyphs) {
            let index = 0;
            for (let c = 0; c < 96; ++c) {
                for (let row = 0; row < 20; ++row) {
                    let data;
                    if (!graphicsGlyphs || !!(c & 32)) {
                        data = combineRows(getLoResGlyphRow(c, row), getLoResGlyphRow(c, row + (row & 1 ? 1 : -1)));
                    } else {
                        data = getLoResGlyphRow(c, row);
                    }
                    dest[index++] =
                        (data & 0x1) * 0x7 +
                        (data & 0x2) * 0x14 +
                        (data & 0x4) * 0x34 +
                        (data & 0x8) * 0xe0 +
                        (data & 0x10) * 0x280 +
                        (data & 0x20) * 0x680 +
                        (data & 0x40) * 0x1c00 +
                        (data & 0x80) * 0x5000 +
                        (data & 0x100) * 0xd000 +
                        (data & 0x200) * 0x38000 +
                        (data & 0x400) * 0xa0000 +
                        (data & 0x800) * 0x1a0000;
                }
            }
        }

        makeHiResGlyphs(this.normalGlyphs, false);

        function setGraphicsBlock(c, x, y, w, h, sep, n) {
            for (let yy = 0; yy < h; ++yy) {
                for (let xx = 0; xx < w; ++xx) {
                    charData[c * 60 + (y + yy) * 6 + (x + xx)] = sep && (xx === 0 || yy === h - 1) ? 0 : n;
                }
            }
        }

        // Build graphics character set
        for (let c = 0; c < 96; ++c) {
            if (!(c & 32)) {
                setGraphicsBlock(c, 0, 0, 3, 3, false, !!(c & 1));
                setGraphicsBlock(c, 3, 0, 3, 3, false, !!(c & 2));
                setGraphicsBlock(c, 0, 3, 3, 4, false, !!(c & 4));
                setGraphicsBlock(c, 3, 3, 3, 4, false, !!(c & 8));
                setGraphicsBlock(c, 0, 7, 3, 3, false, !!(c & 16));
                setGraphicsBlock(c, 3, 7, 3, 3, false, !!(c & 64));
            }
        }

        makeHiResGlyphs(this.graphicsGlyphs, true);

        // Build separated graphics character set
        for (let c = 0; c < 96; ++c) {
            if (!(c & 32)) {
                setGraphicsBlock(c, 0, 0, 3, 3, true, !!(c & 1));
                setGraphicsBlock(c, 3, 0, 3, 3, true, !!(c & 2));
                setGraphicsBlock(c, 0, 3, 3, 4, true, !!(c & 4));
                setGraphicsBlock(c, 3, 3, 3, 4, true, !!(c & 8));
                setGraphicsBlock(c, 0, 7, 3, 3, true, !!(c & 16));
                setGraphicsBlock(c, 3, 7, 3, 3, true, !!(c & 64));
            }
        }

        makeHiResGlyphs(this.separatedGlyphs, true);
    }

    /**
     * Rebuild the 256-entry colour lookup from a 16-entry ABGR collook palette.
     * Each entry encodes: bits 7-5 = background colour index (0-7),
     * bits 4-2 = foreground colour index (0-7), bits 1-0 = blend weight.
     * Reference: b-em mode7_gen_nula_lookup().
     * @param {Uint32Array} collook - 16-entry palette in ABGR format (0xffBBGGRR).
     */
    rebuildColours(collook) {
        const gamma = 1.0 / 2.2;
        for (let i = 0; i < 256; ++i) {
            const weight = (i & 3) / 3.0;
            const fgIndex = (i >> 2) & 7;
            const bgIndex = (i >> 5) & 7;
            const fgColour = collook[fgIndex];
            const bgColour = collook[bgIndex];
            const fgR = (fgColour & 0xff) / 255;
            const fgG = ((fgColour >> 8) & 0xff) / 255;
            const fgB = ((fgColour >> 16) & 0xff) / 255;
            const bgR = (bgColour & 0xff) / 255;
            const bgG = ((bgColour >> 8) & 0xff) / 255;
            const bgB = ((bgColour >> 16) & 0xff) / 255;
            const blendedR = Math.pow(fgR * weight + bgR * (1.0 - weight), gamma) * 240;
            const blendedG = Math.pow(fgG * weight + bgG * (1.0 - weight), gamma) * 240;
            const blendedB = Math.pow(fgB * weight + bgB * (1.0 - weight), gamma) * 240;
            this.colour[i] = blendedR | 0 | ((blendedG | 0) << 8) | ((blendedB | 0) << 16) | (0xff << 24);
        }
    }

    snapshotState() {
        // Encode glyph table references as strings for serialization
        let nextGlyphsName, curGlyphsName, heldGlyphsName;
        for (const [name, table] of [
            ["normal", this.normalGlyphs],
            ["graphics", this.graphicsGlyphs],
            ["separated", this.separatedGlyphs],
        ]) {
            if (this.nextGlyphs === table) nextGlyphsName = name;
            if (this.curGlyphs === table) curGlyphsName = name;
            if (this.heldGlyphs === table) heldGlyphsName = name;
        }
        return {
            prevCol: this.prevCol,
            col: this.col,
            bg: this.bg,
            sep: this.sep,
            dbl: this.dbl,
            oldDbl: this.oldDbl,
            secondHalfOfDouble: this.secondHalfOfDouble,
            wasDbl: this.wasDbl,
            gfx: this.gfx,
            conceal: this.conceal,
            flash: this.flash,
            flashTime: this.flashTime,
            heldChar: this.heldChar,
            holdChar: this.holdChar,
            dataQueue: [...this.dataQueue],
            scanlineCounter: this.scanlineCounter,
            levelDEW: this.levelDEW,
            levelDISPTMG: this.levelDISPTMG,
            levelRA0: this.levelRA0,
            nextGlyphs: nextGlyphsName,
            curGlyphs: curGlyphsName,
            heldGlyphs: heldGlyphsName,
        };
    }

    restoreState(state) {
        this.prevCol = state.prevCol;
        this.col = state.col;
        this.bg = state.bg;
        this.sep = state.sep;
        this.dbl = state.dbl;
        this.oldDbl = state.oldDbl;
        this.secondHalfOfDouble = state.secondHalfOfDouble;
        this.wasDbl = state.wasDbl;
        this.gfx = state.gfx;
        this.conceal = state.conceal ?? false;
        this.flash = state.flash;
        this.flashTime = state.flashTime;
        this.heldChar = state.heldChar;
        this.holdChar = state.holdChar;
        this.dataQueue.set(state.dataQueue);
        this.scanlineCounter = state.scanlineCounter;
        this.levelDEW = state.levelDEW;
        this.levelDISPTMG = state.levelDISPTMG;
        this.levelRA0 = state.levelRA0;

        const glyphMap = {
            normal: this.normalGlyphs,
            graphics: this.graphicsGlyphs,
            separated: this.separatedGlyphs,
        };
        this.nextGlyphs = glyphMap[state.nextGlyphs] || this.normalGlyphs;
        this.curGlyphs = glyphMap[state.curGlyphs] || this.normalGlyphs;
        this.heldGlyphs = glyphMap[state.heldGlyphs] || this.normalGlyphs;
    }

    setNextChars() {
        if (this.gfx) {
            if (this.sep) {
                this.nextGlyphs = this.separatedGlyphs;
            } else {
                this.nextGlyphs = this.graphicsGlyphs;
            }
        } else {
            this.nextGlyphs = this.normalGlyphs;
        }
    }

    handleControlCode(data) {
        const wasGfx = this.gfx;
        const wasHoldChar = this.holdChar;

        switch (data) {
            case 1:
            case 2:
            case 3:
            case 4:
            case 5:
            case 6:
            case 7:
                this.gfx = false;
                this.col = data;
                this.conceal = false;
                this.setNextChars();
                break;
            case 8:
                this.flash = true;
                break;
            case 9:
                this.flash = false;
                break;
            case 12:
            case 13:
                this.dbl = !!(data & 1);
                if (this.dbl) this.wasDbl = true;
                break;
            case 17:
            case 18:
            case 19:
            case 20:
            case 21:
            case 22:
            case 23:
                this.gfx = true;
                this.col = data & 7;
                this.conceal = false;
                this.setNextChars();
                break;
            case 24:
                this.conceal = true;
                break;
            case 25:
                this.sep = false;
                this.setNextChars();
                break;
            case 26:
                this.sep = true;
                this.setNextChars();
                break;
            case 28:
                this.bg = 0;
                break;
            case 29:
                this.bg = this.col;
                break;
            case 30:
                this.holdChar = true;
                break;
            case 31:
                this.holdChar = false;
                break;
        }
        if (wasGfx && (wasHoldChar || this.holdChar) && this.dbl === this.oldDbl) {
            data = this.heldChar;
            this.curGlyphs = this.heldGlyphs;
        } else {
            this.heldChar = 0x20;
            data = 0x20;
        }

        return data;
    }

    fetchData(data) {
        // `copyWithin` over four bytes costs more than the moves it saves.
        this.dataQueue[0] = this.dataQueue[1];
        this.dataQueue[1] = this.dataQueue[2];
        this.dataQueue[2] = this.dataQueue[3];
        this.dataQueue[3] = data & 0x7f;
    }

    setDEW(level) {
        // The SAA5050 input pin "DEW" is connected to the 6845 output pin
        // "VSYNC" and it is used to track frames.
        const oldLevel = this.levelDEW;
        this.levelDEW = level;

        // Trigger on high -> low. This appears to be what the hardware does.
        // It needs to be this way for the scanline counter to stay in sync
        // if you set R6>R4.
        if (!oldLevel || level) {
            return;
        }

        this.scanlineCounter = 0;
        this.secondHalfOfDouble = false;

        // 3:1 flash ratio.
        if (++this.flashTime === 64) this.flashTime = 0;
    }

    // Flashing text starts off in sync with a slow cursor, extinguished together. Multiple MODE
    // changes gradually desynchronise the frame counters.
    // TODO: setDEW is reached a MOS-dependent number of times before Video.frameCount rises, so
    // the initial sync here holds under MOS 1.20 only.
    get hideFlashing() {
        return this.flashTime < 16;
    }

    setDISPTMG(level) {
        // The SAA5050 input pin "LOSE" is connected to the 6845 output pin
        // "DISPTMG" and it is used to track scanlines.
        const oldLevel = this.levelDISPTMG;
        this.levelDISPTMG = level;

        // Trigger on high -> low. This is probably what the hardware does as
        // we need to increment scanline at the end of the scanline, not the
        // beginning.
        if (!oldLevel || level) {
            return;
        }

        this.col = 7;
        this.bg = 0;
        this.holdChar = false;
        this.heldChar = 0x20;
        this.nextGlyphs = this.heldGlyphs = this.normalGlyphs;
        this.flash = false;
        this.sep = false;
        this.gfx = false;
        this.conceal = false;
        this.dbl = false;

        this.scanlineCounter++;
        // Check for end of character row.
        if (this.scanlineCounter === 10) {
            this.scanlineCounter = 0;

            if (this.secondHalfOfDouble) {
                this.secondHalfOfDouble = false;
            } else {
                this.secondHalfOfDouble = this.wasDbl;
            }
        }

        this.wasDbl = false;
    }

    setRA0(level) {
        // The SAA5050 input pin "CRS" is connected to the 6845 output pin
        // "RA0", via a signal inverter, and it is used to select between a
        // normal scanline and a calculated smoothing scanline.
        this.levelRA0 = level;
    }

    /**
     * Clock one character through the chip: take the oldest byte in the pipeline, apply it
     * to the character state, and latch how the resulting cell should look.
     */
    advance() {
        let data = this.dataQueue[0];

        this.oldDbl = this.dbl;

        this.prevCol = this.col;
        this.curGlyphs = this.nextGlyphs;

        let flashThisCell = this.flash;
        let concealThisCell = this.conceal;
        if (data < 0x20) {
            data = this.handleControlCode(data);
        } else if (this.gfx) {
            if (data & 0x20) {
                this.heldChar = data;
                this.heldGlyphs = this.curGlyphs;
            }
        } else {
            this.heldChar = 32;
        }

        // Flash (code 8) is "Set After" — flashThisCell retains the pre-control-code state.
        // Steady (code 9) is "Set At" — update so this cell stops flashing immediately.
        if (flashThisCell && !this.flash) flashThisCell = false;

        // Conceal (code 24) is "Set At", and a colour code only reveals from the cell after itself.
        if (this.conceal) concealThisCell = true;

        this.cellGlyphIndex = (data - 32) * 20;
        this.cellFlash = flashThisCell;
        this.cellConceal = concealThisCell;
        this.cellPalette = ((this.bg & 7) << 5) | ((this.prevCol & 7) << 2);
    }

    glyphScanline() {
        let scanline = this.scanlineCounter << 1;
        if (this.levelRA0) {
            scanline++;
        }

        if (this.oldDbl) {
            scanline = scanline >>> 1;
            if (this.secondHalfOfDouble) {
                scanline += 10;
            }
        }
        return scanline;
    }

    /**
     * Paint the cell most recently latched by `advance`. Everything else read here changes
     * per scanline or per field, not per character.
     */
    emit(buf, offset) {
        const scanline = this.glyphScanline();

        if (this.cellConceal || (this.cellFlash && this.hideFlashing) || (this.secondHalfOfDouble && !this.dbl)) {
            const backgroundColour = this.colour[this.cellPalette & 0xe0];
            for (let i = 0; i < 16; ++i) {
                buf[offset++] = backgroundColour;
            }
        } else {
            let chardef = this.curGlyphs[this.cellGlyphIndex + scanline];
            const paletteIndex = this.cellPalette;

            for (let pixel = 0; pixel < 16; ++pixel) {
                buf[offset + pixel] = this.colour[paletteIndex + (chardef & 3)];
                chardef >>>= 2;
            }
        }
    }

    // The second half of the cell `emit` last painted, for the ULA's 1MHz repaint.
    emitSecondHalf(buf, offset) {
        const scanline = this.glyphScanline();
        if (this.cellConceal || (this.cellFlash && this.hideFlashing) || (this.secondHalfOfDouble && !this.dbl)) {
            const backgroundColour = this.colour[this.cellPalette & 0xe0];
            for (let pixel = 8; pixel < 16; ++pixel) {
                buf[offset + pixel] = backgroundColour;
            }
        } else {
            let chardef = this.curGlyphs[this.cellGlyphIndex + scanline] >>> 16;
            const paletteIndex = this.cellPalette;
            for (let pixel = 8; pixel < 16; ++pixel) {
                buf[offset + pixel] = this.colour[paletteIndex + (chardef & 3)];
                chardef >>>= 2;
            }
        }
    }

    render(buf, offset) {
        this.advance();
        this.emit(buf, offset);
    }
}
